import { useRef } from 'react'
import { CertificateThumbnailImage, Container, Text } from 'react-common'
import { useTranslation } from 'react-i18next'
import { Accordion } from '@platform/ui'
import cn from 'classnames'
import { useIsMobile } from '@/hooks/useIsMobile'
import styles from './CertificateDetails.module.scss'
import { CoverActions } from './components/CoverActions/CoverActions'
import { CurrentResult } from './components/CurrentResults/CurrentResult'
import { useAssignedCertificateRecords } from '../CourseDetails/hooks/useAssignedCertificateRecords'

type Props = {
  objectiveId: string
}

export const CertificateDetails = ({ objectiveId }: Props) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const Heading = isMobile ? Text.H3 : Text.H2
  const headingPadding = isMobile ? Container.Padding.p20 : Container.Padding.p40
  const containerHeaderPadding = isMobile ? undefined : Container.Padding.p40
  const containerPadding = isMobile ? Container.Padding.p40 : Container.Padding.p20

  const cacheKey = useRef(Date.now().toString()).current

  const { certificateRecordData, isCertificateRecordLoading } = useAssignedCertificateRecords({
    objectiveId,
  })

  const canShowCertificateRecord = !!certificateRecordData?.data.length && !isCertificateRecordLoading
  if (!canShowCertificateRecord) {
    return null
  }

  const [firstCertificateRecord] = certificateRecordData.data

  const showAccordion = certificateRecordData.data.length > 1

  return (
    <div className={cn({ [styles.hideAccordion]: !showAccordion })}>
      <Container bottomPadding={containerPadding}>
        <Container bottomPadding={headingPadding}>
          <Heading>{t('courseDetails.certificates.currentResults')}</Heading>
        </Container>
        <CurrentResult certificateRecord={firstCertificateRecord} />
      </Container>
      <Accordion
        isOpen={!showAccordion}
        title={<Heading>{t('courseDetails.certificates.title')}</Heading>}
        size="medium"
        hasTopBorder={false}
        hasBottomBorder={false}
        hasPadding={false}
      >
        {certificateRecordData.data.map((certificateRecord, index) => (
          <Container
            key={certificateRecord.id}
            topPadding={index === 0 ? containerHeaderPadding : undefined}
            bottomPadding={containerPadding}
          >
            <div className={styles.certificateCover}>
              <CertificateThumbnailImage
                cacheKey={cacheKey}
                src={certificateRecord.certificatePngUrl}
                alt="Certificate Preview"
              />
            </div>
            <CoverActions certificateRecord={certificateRecord} />
          </Container>
        ))}
      </Accordion>
    </div>
  )
}
